import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ErrorMessageUtil } from '../../util/error-message-util';
import { TranslateService } from '@ngx-translate/core';
import { Country } from 'src/app/domain/models/Country';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { MessageService } from 'primeng/api';
import { NewFridgeStorage } from 'src/app/domain/models/FridgeStorage';
import { FridgeStorageGateway } from 'src/app/application/gateway/FridgeStorageGateway';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { BasicUser } from 'src/app/domain/models/User';

@Component({
  selector: 'app-form-cold-storage',
  templateUrl: './form-cold-storage.component.html',
  styleUrls: ['./form-cold-storage.component.css'],

})
export class FormColdStorageComponent {

  @Input() isSaveColdStorage: boolean = false;
  @Input() isClosed: boolean = false;
  @Input() exporterEmail: string | undefined;
  @Output() closeDialog = new EventEmitter<boolean>();

  countries: Country[] = []
  loading: boolean = false
  submit: boolean = false
  user: BasicUser | undefined

  fridgestorageFormGroup: FormGroup = this._formBuilder.group({
    fridgestorageName: [null, Validators.required],
    fridgestorageVat: [null],
    fridgestorageContactName: [null, Validators.required],
    fridgestorageContactAddress: [null, Validators.required],
    fridgestorageContactCity: [null, Validators.required],
    fridgestorageContactEmail: [null, [Validators.required, Validators.email]],
    fridgestorageContactPhone: [null, Validators.required],
    fridgestorageContactCountryId: [null, Validators.required],
    fridgestorageFDA: [null]
  })

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly _countryService: CountryGateway,
    private readonly _messageService: MessageService,
    private readonly _fridgeStorageService: FridgeStorageGateway,
    private readonly _authService: AuthGateway
  ) {}

  ngOnInit() {
    this.loadCountries();
    this.user = this._authService.getUser()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isSaveColdStorage']) {
      if (changes['isSaveColdStorage'].previousValue !== undefined) {
        this.submit = true;
        this.saveFridgestorage();
      }
      else {
        this.submit = false;
      }
    }

    if (changes['isClosed']?.currentValue === false) {
      this.fridgestorageFormGroup.reset()
      this.submit = false
      this.loading = false
    }
  }

  loadCountries() {
    this.countries = []
    this.loading = true
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
        this.loading = false
      },
      error: () => {
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') })
      }
    })
  }

  saveFridgestorage() {
    this.loading = true
    if (this.fridgestorageFormGroup.valid) {
      let request = null;
      const newFridgeStorage: NewFridgeStorage = {
        name: this.fridgestorageFormGroup.controls["fridgestorageName"].value,
        vat: this.fridgestorageFormGroup.controls["fridgestorageVat"].value,
        contactName: this.fridgestorageFormGroup.controls["fridgestorageContactName"].value,
        contactAddress: this.fridgestorageFormGroup.controls["fridgestorageContactAddress"].value,
        contactEmail: this.fridgestorageFormGroup.controls["fridgestorageContactEmail"].value,
        contactPhone: this.fridgestorageFormGroup.controls["fridgestorageContactPhone"].value,
        contactCity: this.fridgestorageFormGroup.controls["fridgestorageContactCity"].value,
        contactCountryId: this.fridgestorageFormGroup.controls["fridgestorageContactCountryId"].value,
        FDA: this.fridgestorageFormGroup.controls["fridgeStorageFDA"].value,
        storageType: 1
      }

      if (this.user?.profileType === 'Admin') {
        newFridgeStorage.exporter = this.exporterEmail;
        request = this._fridgeStorageService.createByAdmin(newFridgeStorage)
      }
      else {
        request = this._fridgeStorageService.create(newFridgeStorage)
      }

      request.subscribe({
        next: () => {
          this.closeDialog.emit(true);
          this.fridgestorageFormGroup.reset()
          this.submit = false
          this.loading = false
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('MessageService.FridgeStorage.CreateSuccess') })
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.FridgeStorage.CreateError') });
        }
      })
    }
    else {
      this.loading = false
    }
  }

  getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(formGroup, this.translate, fieldName)
  }
}
